import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLogInteraction } from '@/hooks/useStakeholderInteractions';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { InteractionType } from '@/lib/crm/types';

interface InteractionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  stakeholderId: string;
  stakeholderName: string;
  prefilledDate?: Date;
}

const interactionSchema = z.object({
  date: z.string().min(1, 'Date is required'),
  type: z.enum(['Meeting', 'Call', 'Site Visit', 'Delivery', 'Approval']),
  subject: z.string().min(1, 'Subject is required'),
  outcome: z.string().optional(),
  notes: z.string().optional(),
  attendees: z.string().optional(),
  nextFollowUp: z.string().optional(),
  status: z.enum(['pending', 'completed', 'cancelled']).default('completed'),
});

type InteractionFormData = z.infer<typeof interactionSchema>;

export function InteractionDialog({ 
  isOpen, 
  onClose, 
  stakeholderId, 
  stakeholderName,
  prefilledDate 
}: InteractionDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logInteraction = useLogInteraction();

  const form = useForm<InteractionFormData>({
    resolver: zodResolver(interactionSchema),
    defaultValues: {
      date: prefilledDate ? format(prefilledDate, "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      type: 'Meeting',
      subject: '',
      outcome: '',
      notes: '',
      attendees: '',
      nextFollowUp: '',
      status: 'completed',
    },
  });

  const onSubmit = async (data: InteractionFormData) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await logInteraction.mutateAsync({
        stakeholder_id: stakeholderId,
        date: new Date(data.date).toISOString(),
        type: data.type as InteractionType,
        subject: data.subject,
        outcome: data.outcome || undefined,
        notes: data.notes || undefined,
        attendees: data.attendees || undefined,
        next_follow_up: data.nextFollowUp ? new Date(data.nextFollowUp).toISOString() : undefined,
        status: data.status,
      });

      toast.success('Interaction logged successfully');
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error logging interaction:', error);
      toast.error('Failed to log interaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ghi nhận Tương tác - {stakeholderName}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date & Time *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter subject" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="outcome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Outcome</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter outcome" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter notes..."
                      className="min-h-[80px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="attendees"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Attendees</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter attendees (comma-separated)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nextFollowUp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Next Follow-up</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
